import type { EditingMode } from '@aline/common';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { useCurrentUserId } from 'src/gql';
import type { AlineClientEditor } from 'src/layout-engine';
import { useEditor, useEditorLoading, useMaybeEditor } from 'src/layout-engine';

type EditingHooksContext = {
  authorId: string | null; // friendly shortcut. same as `currentUserId`
  currentUserId: string | null;
  editingMode: EditingMode | null;
  editor: AlineClientEditor;
  editorLoading: boolean;
};

type MaybeEditingHooksContext = {
  authorId: string | null; // friendly shortcut. same as `currentUserId`
  currentUserId: string | null;
  editingMode: EditingMode | null;
  editor: AlineClientEditor | null;
  editorLoading: boolean;
};

/**
 * This hook provides a set of hooks that are useful for almost all editing
 * components.
 *
 * We almost always need the current user ID, the editing mode, the editor, and
 * the editor loading state and we clutter our components with these hooks.
 *
 * This combines them into a single hook.
 */
export function useEditingHooks(): EditingHooksContext {
  const editor = useEditor();
  const editorLoading = useEditorLoading();
  const editingMode = useEditorTabStateSelector(state => state.documentUi.editingMode);
  const currentUserId = useCurrentUserId();
  return {
    authorId: currentUserId, // friendly shortcut. same as `currentUserId`
    currentUserId,
    editingMode,
    editor,
    editorLoading,
  };
}

/**
 * This hook provides a set of hooks that are useful for almost all editing
 * components.
 *
 * We almost always need the current user ID, the editing mode, the editor, and
 * the editor loading state and we clutter our components with these hooks.
 *
 * This combines them into a single hook.
 */
export function useMaybeEditingHooks(): MaybeEditingHooksContext {
  const editor = useMaybeEditor();
  const editorLoading = useEditorLoading();
  const editingMode = useEditorTabStateSelector(state => state.documentUi.editingMode);
  const currentUserId = useCurrentUserId();
  return {
    authorId: currentUserId, // friendly shortcut. same as `currentUserId`
    currentUserId,
    editingMode,
    editor,
    editorLoading,
  };
}
